import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2
} from '@angular/core';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { toDate } from '@df/util/fn/to-date';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { InlineButtonComponent } from '../../../common/buttons/inline-button/inline-button.component';
import { NPageHeader } from '../page-header.component';
import { PageHeaderPromobarCountdownComponent } from './countdown/page-header-promobar-countdown.component';

@Component({
    imports: [
        DfUiCarouselModule,
        PageHeaderPromobarCountdownComponent,
        ICmsClientModule,
        HrefComponent,
        InlineButtonComponent,
        NgTemplateOutlet
    ],
    selector: 'page-header-promobar',
    templateUrl: 'page-header-promobar.html',
    styleUrl: './page-header-promobar.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageHeaderPromobarComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy, OnChanges {
    @Input()
    @ICmsContent({
        watchDepth: 2
    })
    content?: NPageHeader.IBlockDataContent;

    @Input()
    disabled = false;

    @Output()
    visibilityChange = new EventEmitter<boolean>();

    @SessionStorage('prommobarHidden')
    private hidden?: boolean;

    protected countToDate?: Date;
    protected isVisible = false;
    protected isManuallyHidden = false;
    private timeout: number | undefined = 0;

    private readonly renderer = inject(Renderer2);
    private readonly elementRef = inject(ElementRef);

    override ngOnInit(): void {
        if (this.hidden) {
            this.isManuallyHidden = true;
        }
        this.update();
        super.ngOnInit();
    }

    override ngOnChanges(changes): void {
        super.ngOnChanges(changes);
        this.update();
    }

    override ngOnDestroy(): void {
        clearTimeout(this.timeout);
        super.ngOnDestroy();
    }

    private update() {
        const visibility = this.isVisible;
        if (this.isManuallyHidden) {
            this.isVisible = false;
        }
        // no countdown
        else if (!this.content?.promobarMessages?.find(message => message.blocks?.find(block => block.type === 'countdown'))) {
            this.isVisible = true;
        }
        // check if still counts or allows after
        else {
            this.countToDate = this.getCountToDate();

            clearTimeout(this.timeout);
            if (!this.content?.promobarEnabled) {
                this.isVisible = false;
            } else if (!this.content?.promobarDisableAfterCountdown) {
                this.isVisible = true;
            } else if (!this.countToDate) {
                this.isVisible = false;
            } else {
                const diff = this.countToDate.getTime() - Date.now();
                if (diff < 0) {
                    this.isVisible = false;
                } else {
                    this.isVisible = true;
                    // set timeout to re-chceck at the monent it will need to be disabled
                    this.timeout = this.timeoutService.setTimeout(() => this.update(), diff);
                }
            }
        }
        if (visibility !== this.isVisible) {
            this.renderer.setStyle(this.elementRef.nativeElement, 'height', this.isVisible ? '33px' : '0px');
            this.visibilityChange.emit(this.isVisible);
            this.detectChanges();
        }
    }

    private getCountToDate(): Date | undefined {
        if (!this.content?.promobarCountTo) {
            return undefined;
        }
        const date = toDate(this.content.promobarCountTo);
        if (!date) {
            console.error('Invalid promobanner date');
        }
        return date || undefined;
    }
}
